import { queryClient } from "@/config/react-query";

import type {
  CommunicationAccountSummary,
  CommunicationConversationSummary,
  CommunicationMessageSummary,
} from "@/types/communication";

import {
  getAccounts,
  getConversation,
  getConversations,
  getMessages,
  getUnreadCount,
} from "@/api/communication";

export const socialQueryKeys = {
  all: ["comms", "social"] as const,
  unreadCount: () => [...socialQueryKeys.all, "unread-count"] as const,
  accounts: () => [...socialQueryKeys.all, "accounts"] as const,
  conversations: () => [...socialQueryKeys.all, "conversations"] as const,
  conversation: (conversationId?: string) =>
    [...socialQueryKeys.all, "conversation", conversationId] as const,
  messages: (conversationId?: string) =>
    [...socialQueryKeys.all, "messages", conversationId] as const,
};

export const socialQueryOptions = {
  unreadCount: () => ({
    queryKey: socialQueryKeys.unreadCount(),
    queryFn: () => getUnreadCount(),
    staleTime: 30 * 1000,
    select: (data: { count: number } | null) => data?.count ?? 0,
  }),
  accounts: () => ({
    queryKey: socialQueryKeys.accounts(),
    queryFn: () => getAccounts(),
  }),
  conversations: () => ({
    queryKey: socialQueryKeys.conversations(),
    queryFn: async () => {
      const data = await getConversations();
      return data?.conversations ?? [];
    },
  }),
  // A single conversation, used to hydrate a deep-linked thread that isn't on
  // the first conversations page (e.g. a bell notification for an old thread).
  conversation: (conversationId: string) => ({
    queryKey: socialQueryKeys.conversation(conversationId),
    queryFn: () => getConversation(conversationId),
  }),
  messages: (conversationId: string) => ({
    queryKey: socialQueryKeys.messages(conversationId),
    queryFn: async () => {
      const data = await getMessages(conversationId);
      // The API returns newest-first; the thread renders oldest-first, so the
      // cache stores oldest-first and new live messages append to the end.
      return data ? [...data.messages].reverse() : [];
    },
  }),
};

/**
 * Cache updaters shared by useSocialRealtimeCache (websocket-driven) and the
 * inbox page's mutations. They only ever patch caches that already exist — a
 * thread the user never opened has no message cache, so its updates are no-ops,
 * mirroring the chat detail cache in use-chat-realtime-cache.
 */
type ConversationUpdater = (
  conversation: CommunicationConversationSummary,
) => CommunicationConversationSummary;

export function updateSocialConversation(
  conversationId: string,
  updater: ConversationUpdater,
): void {
  queryClient.setQueryData<CommunicationConversationSummary[]>(
    socialQueryKeys.conversations(),
    (prev) => prev?.map((c) => (c.id === conversationId ? updater(c) : c)),
  );
  // A thread deep-linked from a notification may not be on the first page of the
  // list, in which case the open pane reads this per-conversation cache instead.
  // Patch both or that pane goes stale — a new inbound message would leave
  // lastInboundAt behind and keep the composer showing an expired reply window.
  queryClient.setQueryData<CommunicationConversationSummary>(
    socialQueryKeys.conversation(conversationId),
    (prev) => (prev ? updater(prev) : prev),
  );
}

/**
 * This client's current read state for a thread, or undefined when it holds no
 * row for it (list not loaded, or a thread past the first page).
 *
 * Used to dedupe the unread badge: the badge counts unread *conversations*, so a
 * thread this client already has unread must not be counted again, however many
 * events claim to have flipped it.
 */
export function getSocialConversationIsRead(
  conversationId: string,
): boolean | undefined {
  const row =
    queryClient
      .getQueryData<
        CommunicationConversationSummary[]
      >(socialQueryKeys.conversations())
      ?.find((c) => c.id === conversationId) ??
    queryClient.getQueryData<CommunicationConversationSummary>(
      socialQueryKeys.conversation(conversationId),
    );
  return row?.isRead;
}

/**
 * The connected account a live payload names, resolved from the accounts cache.
 *
 * A websocket payload carries only `accountId`, but the sidebar filters on the
 * full `account` object (`c.account?.id === selectedAccountId`), so a new
 * conversation stored with `account: null` is prepended and then immediately
 * filtered back out of view until a refetch or sync. The accounts list is already
 * loaded whenever that filter can be set — it populates the filter dropdown — so
 * resolve the real summary here rather than synthesizing a partial one from the
 * id alone. Returns null when the cache holds no match, which is exactly the
 * behaviour the row had before.
 */
export function getSocialAccountById(
  accountId: string | undefined,
): CommunicationAccountSummary | null {
  if (!accountId) return null;
  return (
    queryClient
      .getQueryData<CommunicationAccountSummary[]>(socialQueryKeys.accounts())
      ?.find((a) => a.id === accountId) ?? null
  );
}

export function prependSocialConversation(
  conversation: CommunicationConversationSummary,
): void {
  queryClient.setQueryData<CommunicationConversationSummary[]>(
    socialQueryKeys.conversations(),
    (prev) => {
      // List not loaded yet — the page fetches a fresh list on mount, so
      // creating a one-item cache here would just be overwritten.
      if (!prev) return prev;
      if (prev.some((c) => c.id === conversation.id)) return prev;
      return [conversation, ...prev];
    },
  );
}

/**
 * Move the sidebar's unread badge by `delta` without refetching it.
 *
 * The badge counts unread *conversations*, and every websocket event that can
 * change that count says so explicitly: an inbound DM carries
 * `conversationBecameUnread` (the read→unread flip), and a read event — the
 * mark-read broadcast, or `conversationBecameRead` on the echo of a reply sent
 * from Meta's own inbox — is only published when the thread actually flipped
 * unread→read. So each one is worth exactly ±1 and the client never has to ask
 * the server for the total.
 *
 * A cache that was never populated is left alone rather than seeded from a delta
 * — the badge query would fetch the real count on its next mount anyway, and
 * inventing one here is how a client-side counter drifts.
 */
export function adjustSocialUnreadCount(delta: number): void {
  queryClient.setQueryData<{ count: number }>(
    socialQueryKeys.unreadCount(),
    (prev) => (prev ? { count: Math.max(0, prev.count + delta) } : prev),
  );
}

type MessageUpdater = (
  message: CommunicationMessageSummary,
) => CommunicationMessageSummary;

export function upsertSocialMessage(
  conversationId: string,
  message: CommunicationMessageSummary,
): void {
  queryClient.setQueryData<CommunicationMessageSummary[]>(
    socialQueryKeys.messages(conversationId),
    (prev) => {
      if (!prev) return prev;
      const idx = prev.findIndex((m) => m.id === message.id);
      if (idx >= 0)
        return prev.map((m, i) =>
          i === idx
            ? {
                ...m,
                ...message,
                // The websocket payload carries no reactions/sender/error columns,
                // so the realtime handler fills them with null. On a redelivery —
                // or any event arriving after the richer REST response — replacing
                // outright would wipe reactions off an already-rendered message.
                // Keep what the cache knows when the incoming row says nothing.
                reactions: message.reactions ?? m.reactions,
                senderProviderId:
                  message.senderProviderId ?? m.senderProviderId,
                errorMessage: message.errorMessage ?? m.errorMessage,
              }
            : m,
        );
      return [...prev, message];
    },
  );
}

export function updateSocialMessage(
  conversationId: string,
  messageId: string,
  updater: MessageUpdater,
): void {
  queryClient.setQueryData<CommunicationMessageSummary[]>(
    socialQueryKeys.messages(conversationId),
    (prev) => prev?.map((m) => (m.id === messageId ? updater(m) : m)),
  );
}
